import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import { tokenizeContentWords } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { Tile, ContentErrorBlock } from '../../detail/tabs/Tile';
import { ConsultationContentType } from '../../detail/tabs/contentTypes';

const SEARCH_DEBOUNCE_MS = 500;

const shouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

const formatTimestamp = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const resolveSpeakerDisplay = (speaker, speakerMap) => {
  if (!speaker) return speaker;
  const mapped = speakerMap?.[speaker] ?? speakerMap?.[String(speaker)];
  return mapped || speaker;
};

const getSpeakerLabel = (speaker, speakerMap) => {
  const display = resolveSpeakerDisplay(speaker, speakerMap);
  if (!display) return 'Unknown:';
  const normalized = display.toLowerCase();
  if (normalized.includes('doctor') || normalized.includes('gp')) {
    return 'Clinician:';
  }
  return `${display}:`;
};

const getSpeakerClass = (speaker, speakerMap) => {
  const display = resolveSpeakerDisplay(speaker, speakerMap);
  if (!display) return 'unknown';
  const normalized = display.toLowerCase();
  if (normalized.includes('doctor') || normalized.includes('clinician') || normalized.includes('gp')) {
    return 'clinician';
  }
  if (normalized.includes('patient')) {
    return 'patient';
  }
  return 'other';
};

const sameSpeaker = (a, b) => {
  return (a?.speaker || '').toLowerCase() === (b?.speaker || '').toLowerCase();
};

const findMatchedWordRanges = (text, quoteWordSet) => {
  if (!text || !quoteWordSet?.size) return [];

  const ranges = [];
  const pattern = /[a-zA-Z0-9-]+/g;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (quoteWordSet.has(match[0].toLowerCase())) {
      ranges.push({ start: match.index, end: match.index + match[0].length });
    }
  }

  return ranges;
};

/**
 * Find a quote within one segment, or across consecutive same-speaker segments
 * (joined with a single space). Returns null or { startIndex, ranges }.
 * ranges[segmentIndex] = { start, end } character offsets in that segment's text.
 */
const findExactQuoteMatch = (segments, quote) => {
  const trimmed = quote?.trim();
  if (!trimmed || !segments?.length) return null;
  const needle = trimmed.toLowerCase();

  for (let start = 0; start < segments.length; start++) {
    let joined = '';
    const partMeta = [];

    for (let end = start; end < segments.length; end++) {
      if (end > start && !sameSpeaker(segments[end], segments[start])) {
        break;
      }

      const text = typeof segments[end].text === 'string' ? segments[end].text : '';
      if (end > start) {
        joined += ' ';
      }
      partMeta.push({ index: end, startInJoined: joined.length, length: text.length });
      joined += text;

      const matchIdx = joined.toLowerCase().indexOf(needle);
      if (matchIdx === -1) {
        continue;
      }

      const matchEnd = matchIdx + needle.length;
      const ranges = {};
      for (const meta of partMeta) {
        const segStart = meta.startInJoined;
        const segEnd = meta.startInJoined + meta.length;
        const overlapStart = Math.max(matchIdx, segStart);
        const overlapEnd = Math.min(matchEnd, segEnd);
        if (overlapStart < overlapEnd) {
          ranges[meta.index] = {
            start: overlapStart - segStart,
            end: overlapEnd - segStart,
          };
        }
      }

      const matchedIndices = Object.keys(ranges).map(Number).sort((a, b) => a - b);
      if (matchedIndices.length === 0) {
        continue;
      }

      return {
        startIndex: matchedIndices[0],
        fuzzy: false,
        ranges,
      };
    }
  }

  return null;
};

/**
 * Fallback when exact quote match fails: pick the same-speaker merged window with
 * the highest distinct content-word coverage (stop words excluded).
 */
const findFuzzyQuoteMatch = (segments, quote) => {
  const trimmed = quote?.trim();
  if (!trimmed || !segments?.length) return null;

  const quoteWords = tokenizeContentWords(trimmed);
  if (quoteWords.length === 0) return null;

  let bestMatch = null;

  for (let start = 0; start < segments.length; start++) {
    let joined = '';
    const partMeta = [];

    for (let end = start; end < segments.length; end++) {
      if (end > start && !sameSpeaker(segments[end], segments[start])) {
        break;
      }

      const text = typeof segments[end].text === 'string' ? segments[end].text : '';
      if (end > start) {
        joined += ' ';
      }
      partMeta.push({ index: end, length: text.length });
      joined += text;

      const windowWordSet = new Set(tokenizeContentWords(joined));
      const matchedCount = quoteWords.filter((word) => windowWordSet.has(word)).length;
      const coverage = matchedCount / quoteWords.length;

      if (
        !bestMatch
        || coverage > bestMatch.coverage
        || (coverage === bestMatch.coverage && start < bestMatch.startIndex)
      ) {
        bestMatch = {
          startIndex: partMeta[0].index,
          coverage,
          partMeta,
        };
      }
    }
  }

  if (!bestMatch) return null;

  const quoteWordSet = new Set(quoteWords);
  const wordRanges = {};

  for (const meta of bestMatch.partMeta) {
    const text = typeof segments[meta.index].text === 'string' ? segments[meta.index].text : '';
    const ranges = findMatchedWordRanges(text, quoteWordSet);
    if (ranges.length > 0) {
      wordRanges[meta.index] = ranges;
    }
  }

  const matchedIndices = Object.keys(wordRanges).map(Number).sort((a, b) => a - b);

  return {
    startIndex: matchedIndices[0] ?? bestMatch.startIndex,
    fuzzy: true,
    wordRanges,
  };
};

const findQuoteMatch = (segments, quote) => {
  return findExactQuoteMatch(segments, quote) ?? findFuzzyQuoteMatch(segments, quote);
};

/**
 * Map a match in a joined same-speaker string back to per-segment character ranges.
 * Joiner spaces between segments are not part of any segment text.
 */
const mapJoinedMatchToRanges = (partMeta, matchIdx, matchEnd) => {
  const ranges = {};
  for (const meta of partMeta) {
    const segStart = meta.startInJoined;
    const segEnd = meta.startInJoined + meta.length;
    const overlapStart = Math.max(matchIdx, segStart);
    const overlapEnd = Math.min(matchEnd, segEnd);
    if (overlapStart < overlapEnd) {
      ranges[meta.index] = {
        start: overlapStart - segStart,
        end: overlapEnd - segStart,
      };
    }
  }
  return ranges;
};

/**
 * Find all exact (case-insensitive) substring matches within each maximal
 * same-speaker run, treating consecutive same-speaker segments as one string.
 */
const findAllExactSearchMatches = (segments, query) => {
  const trimmed = query?.trim();
  if (!trimmed || !segments?.length) return [];

  const needle = trimmed.toLowerCase();
  const needleLen = needle.length;
  const matches = [];

  let runStart = 0;
  while (runStart < segments.length) {
    let runEnd = runStart;
    while (
      runEnd + 1 < segments.length
      && sameSpeaker(segments[runEnd + 1], segments[runStart])
    ) {
      runEnd += 1;
    }

    let joined = '';
    const partMeta = [];
    for (let i = runStart; i <= runEnd; i++) {
      const text = typeof segments[i].text === 'string' ? segments[i].text : '';
      if (i > runStart) {
        joined += ' ';
      }
      partMeta.push({ index: i, startInJoined: joined.length, length: text.length });
      joined += text;
    }

    const joinedLower = joined.toLowerCase();
    let from = 0;
    while (from <= joinedLower.length - needleLen) {
      const matchIdx = joinedLower.indexOf(needle, from);
      if (matchIdx === -1) break;

      const matchEnd = matchIdx + needleLen;
      const ranges = mapJoinedMatchToRanges(partMeta, matchIdx, matchEnd);
      const matchedIndices = Object.keys(ranges).map(Number).sort((a, b) => a - b);
      if (matchedIndices.length > 0) {
        matches.push({
          startIndex: matchedIndices[0],
          ranges,
        });
      }

      from = matchIdx + Math.max(needleLen, 1);
    }

    runStart = runEnd + 1;
  }

  return matches;
};

const renderSegmentText = (text, highlights) => {
  if (!highlights || !text) return text;

  const ranges = Array.isArray(highlights) ? highlights : [highlights];
  if (ranges.length === 0) return text;

  const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
  const parts = [];
  let cursor = 0;

  for (const range of sortedRanges) {
    if (range.start > cursor) {
      parts.push(text.slice(cursor, range.start));
    }
    const className = range.active ? 'quote-match search-match-active' : 'quote-match';
    parts.push(
      <mark key={`${range.start}-${range.end}-${range.active ? 'a' : 'i'}`} className={className}>
        {text.slice(range.start, range.end)}
      </mark>
    );
    cursor = range.end;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return <>{parts}</>;
};

/**
 * Build per-segment highlight ranges from search matches, tagging the active match.
 * ranges[segmentIndex] = [{ start, end, active }, ...]
 */
const buildSearchHighlightsBySegment = (searchMatches, activeMatchIndex) => {
  const bySegment = {};

  searchMatches.forEach((match, matchIndex) => {
    const isActive = matchIndex === activeMatchIndex;
    Object.entries(match.ranges || {}).forEach(([segIndex, range]) => {
      const idx = Number(segIndex);
      if (!bySegment[idx]) bySegment[idx] = [];
      bySegment[idx].push({
        start: range.start,
        end: range.end,
        active: isActive,
      });
    });
  });

  return bySegment;
};

const TranscriptTab = ({
  consultation,
  audioCurrentTime,
  onSeekAudio,
  highlightQuote,
  highlightQuoteKey,
  isRegenerationEnabled,
  handleRegenerationClick,
  regenerateService
}) => {
  const segmentRefs = useRef([]);
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const [searchInput, setSearchInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  // One active highlight source: idle | search | quote
  const [highlightMode, setHighlightMode] = useState('idle');

  const status = consultation?.status;
  const consultationId = consultation?.consultationId;
  const segments = consultation?.transcriptSegments;
  const speakerMap = consultation?.transcriptSpeakers;

  const isSearchMode = highlightMode === 'search' && Boolean(appliedQuery.trim());
  const isQuoteMode = highlightMode === 'quote' && Boolean(highlightQuote?.trim());

  const currentSegmentIndex = useMemo(() => {
    if (!segments || segments.length === 0) return -1;

    return segments.findIndex(
      seg => audioCurrentTime >= seg.startSeconds && audioCurrentTime < seg.endSeconds
    );
  }, [segments, audioCurrentTime]);

  const quoteMatch = useMemo(
    () => (isQuoteMode ? findQuoteMatch(segments, highlightQuote) : null),
    [segments, highlightQuote, isQuoteMode]
  );

  const searchMatches = useMemo(
    () => findAllExactSearchMatches(segments, appliedQuery),
    [segments, appliedQuery]
  );

  const searchHighlightsBySegment = useMemo(
    () => buildSearchHighlightsBySegment(searchMatches, activeMatchIndex),
    [searchMatches, activeMatchIndex]
  );

  // Quote click always wins immediately (keeps search input text)
  useEffect(() => {
    if (!highlightQuote?.trim()) return;
    setHighlightMode('quote');
  }, [highlightQuote, highlightQuoteKey]);

  // Debounce applied query; clear immediately when input emptied
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const trimmed = searchInput.trim();
    if (!trimmed) {
      setAppliedQuery('');
      setActiveMatchIndex(0);
      return undefined;
    }

    debounceTimerRef.current = setTimeout(() => {
      setAppliedQuery(searchInput);
      setActiveMatchIndex(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Clamp active index when match list changes
  useEffect(() => {
    if (searchMatches.length === 0) {
      setActiveMatchIndex(0);
      return;
    }
    setActiveMatchIndex((prev) => (prev >= searchMatches.length ? 0 : prev));
  }, [searchMatches]);

  useEffect(() => {
    if (isSearchMode || isQuoteMode) return;
    if (currentSegmentIndex >= 0 && segmentRefs.current[currentSegmentIndex]) {
      segmentRefs.current[currentSegmentIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [currentSegmentIndex, isSearchMode, isQuoteMode]);

  useEffect(() => {
    if (!isQuoteMode || !quoteMatch) return;

    const segment = segments[quoteMatch.startIndex];
    const scrollToMatch = () => {
      if (segmentRefs.current[quoteMatch.startIndex]) {
        segmentRefs.current[quoteMatch.startIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };

    // Defer so the tab is visible after switching from Notes
    requestAnimationFrame(scrollToMatch);

    if (onSeekAudio && segment?.startSeconds !== undefined) {
      onSeekAudio(segment.startSeconds);
    }
  }, [highlightQuote, highlightQuoteKey, quoteMatch, segments, onSeekAudio, isQuoteMode]);

  // Scroll + seek for active search match
  useEffect(() => {
    if (!isSearchMode || searchMatches.length === 0) return;

    const match = searchMatches[activeMatchIndex];
    if (!match) return;

    const segment = segments?.[match.startIndex];
    const scrollToMatch = () => {
      if (segmentRefs.current[match.startIndex]) {
        segmentRefs.current[match.startIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };

    requestAnimationFrame(scrollToMatch);

    if (onSeekAudio && segment?.startSeconds !== undefined) {
      onSeekAudio(segment.startSeconds);
    }
  }, [isSearchMode, searchMatches, activeMatchIndex, segments, onSeekAudio]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    if (value.trim()) {
      setHighlightMode('search');
    } else {
      // Clearing leaves quote mode intact if a quote was showing
      setHighlightMode((prev) => (prev === 'quote' ? 'quote' : 'idle'));
    }
  };

  const goToPrevMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setActiveMatchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches.length]);

  const goToNextMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    setActiveMatchIndex((prev) => (prev + 1) % searchMatches.length);
  }, [searchMatches.length]);

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    // Apply immediately so Enter can navigate before debounce settles
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const nextQuery = searchInput;
    const wasSearchMode = highlightMode === 'search';
    if (nextQuery.trim()) {
      setHighlightMode('search');
    }
    if (nextQuery.trim() !== appliedQuery.trim()) {
      setAppliedQuery(nextQuery);
      setActiveMatchIndex(0);
      return;
    }

    // Reclaiming search from quote: land on current match without advancing yet
    if (!wasSearchMode) {
      return;
    }

    if (event.shiftKey) {
      goToPrevMatch();
    } else {
      goToNextMatch();
    }
  };

  const handleSegmentClick = (segment) => {
    if (onSeekAudio && segment.startSeconds !== undefined) {
      onSeekAudio(segment.startSeconds);
    }
  };

  const matchCount = searchMatches.length;
  const counterLabel = isSearchMode
    ? (matchCount === 0 ? '0' : `${activeMatchIndex + 1} / ${matchCount}`)
    : null;
  const navDisabled = !isSearchMode || matchCount === 0;
  const hasSegments = segments && segments.length > 0;

  return (
    <div className="transcript-tab" ref={containerRef}>
      <Tile
        doc={consultation}
        entityId={consultationId}
        contentType={ConsultationContentType.TRANSCRIPT_STATUS}
        title="Transcript"
        condition={shouldShowContent(segments, status)}
        isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
        onRegenerateClick={handleRegenerationClick}
        regenerateService={regenerateService}
        showContentOnError
        shouldShowError={() => !(segments?.length > 0)}
      >
        <ContentErrorBlock
          doc={consultation}
          entityId={consultationId}
          contentType={ConsultationContentType.TRANSCRIPT_SPEAKERS_STATUS}
          isRetryEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
          onRegenerateClick={handleRegenerationClick}
          regenerateService={regenerateService}
          shouldShowError={() => !consultation?.transcriptSpeakers || Object.keys(consultation.transcriptSpeakers).length === 0}
        />
        {!hasSegments ? (
          <div className="transcript-empty">
            <p>No transcript available</p>
          </div>
        ) : (
          <>
            <div className="transcript-search-bar">
        <div className="transcript-search-input-container">
          <FontAwesomeIcon icon={faSearch} className="transcript-search-icon" />
          <input
            type="search"
            className="transcript-search-input"
            placeholder="Search transcript..."
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search transcript"
          />
          <div className="transcript-search-nav">
            {counterLabel !== null && (
              <span className="transcript-search-count">{counterLabel}</span>
            )}
            <button
              type="button"
              className="transcript-search-nav-btn"
              onClick={goToPrevMatch}
              disabled={navDisabled}
              aria-label="Previous search result"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type="button"
              className="transcript-search-nav-btn"
              onClick={goToNextMatch}
              disabled={navDisabled}
              aria-label="Next search result"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>
      <div className="transcript-segments">
        {segments.map((segment, index) => {
          let segmentHighlights;
          let hasQuoteHighlight = false;

          if (isSearchMode) {
            segmentHighlights = searchHighlightsBySegment[index];
            hasQuoteHighlight = Boolean(segmentHighlights?.length);
          } else if (isQuoteMode) {
            const isFuzzyMatch = Boolean(quoteMatch?.fuzzy);
            segmentHighlights = isFuzzyMatch
              ? quoteMatch?.wordRanges?.[index]
              : quoteMatch?.ranges?.[index];
            hasQuoteHighlight = isFuzzyMatch
              ? Boolean(segmentHighlights?.length)
              : Boolean(segmentHighlights);
          }

          const isAudioHighlight = index === currentSegmentIndex;
          return (
            <div
              key={index}
              ref={el => segmentRefs.current[index] = el}
              className={`transcript-segment ${getSpeakerClass(segment.speaker, speakerMap)}${isAudioHighlight ? ' highlighted' : ''}${hasQuoteHighlight ? ' quote-highlighted' : ''}`}
              onClick={() => handleSegmentClick(segment)}
            >
              <div className="segment-header">
                <span className={`speaker-label ${getSpeakerClass(segment.speaker, speakerMap)}`}>
                  {getSpeakerLabel(segment.speaker, speakerMap)}
                </span>
                <span className="segment-timestamp">
                  {formatTimestamp(segment.startSeconds)}
                </span>
              </div>
              <div className="segment-text">
                {renderSegmentText(segment.text, segmentHighlights)}
              </div>
            </div>
          );
        })}
      </div>
          </>
        )}
      </Tile>
    </div>
  );
};

export default TranscriptTab;
